import {Component, OnInit, OnDestroy, Input, EventEmitter, Output} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {CloudApiService} from "../../services/cloud-api.service";
import {UserSessionService} from "../../services/user-session.service";

import {DashboardDto} from "../../services/dtos/dashboard.dto";
import {SearchDto} from "../../services/dtos/search.dto";

import moment = require("moment");
import {NgbPopoverConfig} from '@ng-bootstrap/ng-bootstrap';
import {Observable, Subscription} from "rxjs";
import {BranchDto} from "../../services/dtos/branch.dto";
import {SecurityGateAlarmDto} from "../../services/dtos/security-gate-alarm.dto";

enum State {
    None,
    Loading,
    Ready
}
// our version of lib.dom.d.ts doesn't contain Notifcation.permission, declaring Notification as any type
// will temporarily fix "Property 'permission' does not exist on type" error until we update Typescript
declare var Notification: any;

@Component({
    selector: 'alarm',
    template: require('./alarm.component.html'),
    styles: [ require('./alarm.component.css') ],
    providers: [NgbPopoverConfig]
})
export class AlarmComponent implements OnInit, OnDestroy {
    @Input() isFullscreen: boolean = true;
    @Input() selectedBranch: number;
    @Output() onErrorMessage: EventEmitter<{ message: object, invalidateState: boolean }> = new EventEmitter<{ message: object, invalidateState: boolean }>();

    states = State;
    state: State = State.None;
    errorMessage: string;

    branches: BranchDto[] = [];
    dashboardData: DashboardDto = new DashboardDto();

    isAlarmNotificationEnabled: boolean;

    private pollingTimerId: number;
    private librarySubscription: Subscription;

    constructor(
        private route: ActivatedRoute,
        private userSession: UserSessionService,
        private cloudApiService: CloudApiService,
        config: NgbPopoverConfig
    ) {
        config.placement = 'left';
        config.triggers = 'hover'
    }

    public ngOnInit(): void {
        this.getIsAlarmNotificationEnabledFromCookie();

        this.route.queryParams.subscribe(params => {
            this.librarySubscription = this.userSession.library.subscribe(library => {
                this.state = State.Loading;
                if (library)
                    this.getBranches().subscribe(() => {
                        if (this.isFullscreen)
                            this.updateData(params['branch']);
                        else
                            this.updateData(this.selectedBranch);
                    },
                    error => {
                        this.errorMessage = error;
                        this.state = State.None;
                    })
                    this.getBranches();
            });
        });
    }

    public ngOnDestroy(): void {
        if (this.librarySubscription != null) this.librarySubscription.unsubscribe();
        window.clearTimeout(this.pollingTimerId);
    }

    public updateData(branchId: number, showLoading: boolean = true): void {
        this.errorMessage = null;
        if (showLoading)
            this.state = State.Loading;

        if (typeof branchId !== 'undefined') {
            this.retrieveStatistics(branchId).subscribe(() => {
                this.state = State.Ready;
            });

            if (this.isFullscreen) {
                // Refresh report data every 30 seconds
                window.clearTimeout(this.pollingTimerId);
                this.pollingTimerId = window.setTimeout(() => {
                    this.updateData(branchId,false);
                }, 30 * 1000);
            }
        }
    }

    public getAlarmNotificationToggleButtonClass() : string {
        if (this.isAlarmNotificationEnabled)
            return "btn-danger";
        return "btn-success";
    }

    public getAlarmNotificationToggleButtonTooltip() : string {
        if (this.isAlarmNotificationEnabled)
            return "Disable desktop notifications";
        return "Enable desktop notifications";
    }

    public getAlarmNotificationToggleButtonIcon() : string {
        if (this.isAlarmNotificationEnabled)
            return "fa-bell-slash";
        return "fa-bell";
    }

    public setAlarmNotifications(enabled: boolean) : void {
        if (enabled) {
            if (Notification.permission === "default") {
                Notification.requestPermission().then((result: NotificationPermission) => {
                    this.isAlarmNotificationEnabled = result === "granted";
                    document.cookie = "alarm_notifications="+this.isAlarmNotificationEnabled+"; max-age=34560000; path=/";
                });
            }
            else if (Notification.permission === "denied") {
                this.isAlarmNotificationEnabled = false;
                this.handleError("Could not enable desktop notifications as the notification permission for this website has been manually denied. Please reset or enable it in your browser settings.", false);
            } else {
                this.isAlarmNotificationEnabled = enabled;
            }
        } else {
            this.isAlarmNotificationEnabled = enabled;
        }
        document.cookie = "alarm_notifications="+this.isAlarmNotificationEnabled+"; max-age=34560000; path=/";
    }

    private getIsAlarmNotificationEnabledFromCookie() {
        const value = "; " + document.cookie;
        const parts = value.split("; alarm_notifications=");

        if (parts.length == 2) {
            this.setAlarmNotifications(JSON.parse(parts.pop().split(";").shift()));
            document.cookie = "alarm_notifications="+this.isAlarmNotificationEnabled+"; max-age=34560000; path=/";
        }
    }

    private buildDesktopNotification(newAlarmCount: number, alarms: SecurityGateAlarmDto[]) : Notification {
        let title = "New Alarms";
        let body = `${alarms.length} or more new alarms`;

        if (newAlarmCount == 1 || (newAlarmCount == -1 && alarms.length == 1)) {
            title = "New Alarm";
            body = `${alarms[0].assetNumber} - ${alarms[0].title}`;
        }
        if (newAlarmCount > 1) {
            body = `${newAlarmCount} new alarms`;
        }

        let notification = new Notification(title, {
            tag: "fe-cloud-dashboard-alarm",
            body: body,
            icon: "assets/images/application-icons/AlarmViewer.png",
            renotify: true,
        });
        notification.onclick = () => window.focus();

        return notification;
    }

    private spawnDesktopNotification(newAlarmCount: number, alarms: SecurityGateAlarmDto[]) : void {
        // Check if the user has granted permission for notifications
        if (Notification.permission === "granted" && this.isAlarmNotificationEnabled) {
            this.buildDesktopNotification(newAlarmCount, alarms);
        } else if (this.isAlarmNotificationEnabled) {
            Notification.requestPermission().then((result : NotificationPermission) => {
                if (result === "granted") {
                    this.buildDesktopNotification(newAlarmCount, alarms);
                }
            });
        }
    }

    private retrieveStatistics(branchId: number): Observable<void> {
        let searchQuery = this.createSearchQuery();
        if (this.isFullscreen) searchQuery.alarmRows = 50;
        return this.cloudApiService.getDashboard(branchId, searchQuery)
            .map(dashboard => {
                let lastKnownAlarmId = this.dashboardData == null || this.dashboardData.alarms == null || this.dashboardData.alarms.length < 1 ? null : this.dashboardData.alarms[0].id;
                let newDashboardData = new DashboardDto().fromObject(dashboard);

                let newAlarms = -1;
                if (newDashboardData != null && newDashboardData.alarms != null && newDashboardData.alarms.length > 0 && this.state != State.Loading) {
                    if (lastKnownAlarmId != null) {
                        newAlarms = newDashboardData.alarms.findIndex(x => x.id == lastKnownAlarmId);

                        if (newAlarms == 0) {
                            newDashboardData = this.dashboardData;
                        } else {
                            if (newAlarms > 0) {
                                for (let i = 0; i < newAlarms; i++) {
                                    newDashboardData.alarms[i].isNewAlarm = true;
                                }
                            } else {
                                newDashboardData.alarms.forEach(x => x.isNewAlarm = true)
                            }
                            this.spawnDesktopNotification(newAlarms, newDashboardData.alarms);
                        }
                    } else {
                        newDashboardData.alarms.forEach(x => x.isNewAlarm = true);
                        this.spawnDesktopNotification(newAlarms, newDashboardData.alarms);
                    }
                }

                this.dashboardData = newDashboardData;
            })
            .catch(error => this.handleError(error));
    }

    private createSearchQuery(): SearchDto {
        let searchQuery = new SearchDto();

        searchQuery.fromDateUtc = moment().startOf('day').toDate();
        searchQuery.toDateUtc = moment(searchQuery.fromDateUtc).add({'hours': 24}).toDate();

        return searchQuery;
    }

    private handleError(error: any, invalidateState: boolean = true): Observable<any> {
        if (!this.isFullscreen) {
            this.onErrorMessage.emit({message: error, invalidateState: invalidateState});
        }

        if (invalidateState)
            this.state = State.None;

        this.errorMessage = error;
        return Observable.throw(error);
    }

    public getBranchName(branchId: number) : string {
        for (let i = 0; i < this.branches.length; i++) {
            if (this.branches[i].id == branchId)
                return this.branches[i].name
        }
    }

    private getBranches(): Observable<void>  {
        return this.cloudApiService.getBranches()
            .map(branches => this.branches = branches)
            .catch(error => this.handleError(error)
        );
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/components/alarms/alarm.component.ts